import React, { useState, useEffect, useMemo, useContext } from 'react';
import { getConsolidateVariableContract } from '../../utils/services/channelAPI';
import { getAllLanguage } from '../../utils/services/languageApi';
import { TemplateSelector } from './TemplateSelector';
import { DynamicForm } from './DynamicForm';
import { DocumentPreview } from './DocumentPreview';
import styled from 'styled-components';
import { message } from 'antd';
import { AppStoreContext } from '../../context/appStoreContext';
import { getTodaysDateWithFormat, saveByteArray } from '../../utils/javascript';

import {
  ALL_VARIABLE_OBJECT_TYPES,
  VARIABLE_OBJECT_TYPES,
  VARIABLE_TYPE,
} from '../../utils/constants/appConstants';
import { getCommunicationLanguages, initiateTransaction } from '../../utils/services/documentAPI';
import {
  getJsonFromArraybuffer,
  handleDataValidationErrors,
} from '../../utils/dataValidationHelper';
import { COMMUNICATION_GENERATOR } from './translationValues';
import { useTranslation } from 'react-i18next';

const NAMESPACE = ['COMMUNICATION_GENERATOR'];

const AppContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu',
    'Cantarell', sans-serif;
  margin-left: 5%;
  height: 100%;
  width: 95%;
  overflow-y: auto;
`;

const ContentWrapper = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const AppTitle = styled.h1`
  color: #1a1a1a;
  text-align: center;
  margin-bottom: 12px;
  font-size: 2.5rem;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const AppSubtitle = styled.p`
  color: #666;
  text-align: center;
  margin: 0 0 40px 0;
  font-size: 1.1rem;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

function CommunicationGenerator() {
  const { globalVariables } = useContext(AppStoreContext);

  const [communicationDetails, setCommunicationDetails] = useState();

  const [filteredCommunications, setFilteredCommunications] = useState([]);

  const [selectedChannel, setSelectedChannel] = useState('');

  const [toAddresses, setToAddresses] = useState([]);
  const [ccAddresses, setCcAddresses] = useState([]);

  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [showDocument, setShowDocument] = useState(false);

  const [showLoader, setShowLoader] = useState(false);
  const [showDownloadLoader, setShowDownloadLoader] = useState(false);

  const [formVariables, setFormVariables] = useState([]);
  const [examplValuesData, setExamplValuesData] = useState({});
  const [errorMessages, setErrorMessages] = useState({});

  const { t } = useTranslation(NAMESPACE);

  const isEmail = selectedChannel === 'email';

  const fetchConsolidatedVariables = async () => {
    setFormVariables([]);
    const payload = {
      communicationId: communicationDetails?.communicationId,
      channel: selectedChannel,
      language: selectedLanguage,
    };
    setShowLoader(true);
    try {
      const res = await getConsolidateVariableContract(payload, 'live');
      if (res.statusCode === 200 && res.data) {
        setFormVariables(res.data.variableContract);
        return true;
      } else {
        message.error(
          t(
            COMMUNICATION_GENERATOR.GENERATE_FORM_FAILED.key,
            COMMUNICATION_GENERATOR.GENERATE_FORM_FAILED.value,
          ),
        );
        return false;
      }
    } catch (e) {
      console.error(e);
      message.error(
        t(
          COMMUNICATION_GENERATOR.GENERATE_FORM_FAILED.key,
          COMMUNICATION_GENERATOR.GENERATE_FORM_FAILED.value,
        ),
      );
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  const modifyContract = (variables, globalVariables, parentName = '') => {
    return variables.map(v => {
      v.tempName = v.name;
      if (v.variableId) {
        const variableDetails = globalVariables.find(g => g.id === v.variableId);
        return { ...variableDetails, isMandatory: v.isMandatory };
      }
      if (v.content && Array.isArray(v.content)) {
        if (v.type === 'object') {
          v.type = VARIABLE_TYPE.TABLE_OBJECT;
        }
        if (v.content?.length > 0) {
          if (v.type === VARIABLE_TYPE.TABLE_OBJECT) {
            const parName = parentName ? `${parentName}.${v.name}` : v.name;
            v.name = parName;
            v.variables = modifyContract(v.content, globalVariables, parName);
            v.content = undefined;
          } else {
            v.variables = modifyContract(v.content, globalVariables);
            v.content = undefined;
          }
        } else {
          v.variables = [];
          v.content = undefined;
        }
      }
      return { ...v, placeHolderText: v.tempName, tempName: undefined };
    });
  };

  const documentVariables = useMemo(() => {
    let docVariables = formVariables;
    if (globalVariables?.length) {
      docVariables = modifyContract(formVariables, globalVariables, undefined);
    }
    return docVariables;
  }, [formVariables, globalVariables]);

  useEffect(() => {
    if (documentVariables?.length > 0 && globalVariables?.length > 0) {
      const getSampleData = (variables, parent = undefined) => {
        let sampleData = {};
        let newBlockName = `block1`;
        variables.forEach((element, index) => {
          if (element.type === 'ListString' || element.type === 'listOfStrings') {
            sampleData = {
              ...sampleData,
              [element.placeHolderText]: {
                key: element.placeHolderText,
                value: [],
              },
            };
          } else if (element.type === VARIABLE_TYPE.CHART_OBJECT) {
            const objArray = [];
            objArray.push({
              key: element.name,
              subKey: newBlockName,
              name: element.placeHolderText,
              type: element.type,
              isObject: true,
              value: constructChartVariable(element.variables),
              parent,
            });
            sampleData = {
              ...sampleData,
              [element.placeHolderText]: {
                value: [{ key: newBlockName, value: objArray }],
                type: element.type,
                key: element.name,
              },
            };
            // console.log("element name :: ", element.name, sampleData);
          } else if (element.type === VARIABLE_TYPE.FORM_OBJECT) {
            const objArray = [];
            objArray.push({
              key: element.name,
              subKey: newBlockName,
              name: element.placeHolderText,
              type: element.type,
              isObject: true,
              value: constructChartVariable(element.variables, true),
              parent,
            });
            sampleData = {
              ...sampleData,
              [element.placeHolderText]: {
                value: [{ key: newBlockName, value: objArray }],
                type: element.type,
                key: element.name,
              },
            };
          } else if (
            VARIABLE_OBJECT_TYPES.includes(element.type) ||
            element.type === VARIABLE_TYPE.TABLE_OBJECT
          ) {
            let objArray = [];
            element.variables.forEach(eachVar => {
              objArray.push({
                key: element.name,
                subKey: newBlockName,
                name: eachVar.placeHolderText,
                type: eachVar.type,
                isObject: true, // TODO : Continue from here to do CRUD operations
                parent,
                isMandatory: eachVar.isMandatory,
                value:
                  eachVar.type === 'Date'
                    ? getTodaysDateWithFormat()
                    : eachVar.type === 'ListString' || eachVar.type === 'listOfStrings'
                    ? []
                    : eachVar.type === VARIABLE_TYPE.CHART_OBJECT
                    ? constructChartVariable(eachVar.variables)
                    : eachVar.type === 'object' || eachVar.type === VARIABLE_TYPE.TABLE_OBJECT
                    ? getSampleData([eachVar], `${parent ? `${parent}.` : ''}${newBlockName}`)[
                        eachVar.placeHolderText
                      ]
                    : '',
                extraInfo: eachVar?.extraInfo,
                listInfo: eachVar?.listInfo,
                numberInfo: eachVar?.numberInfo,
                isArray: eachVar.isArray || false,
              });
            });
            sampleData = {
              ...sampleData,
              [element.placeHolderText]: {
                value: [{ key: newBlockName, value: objArray }],
                type: element.type,
                key: element.name,
              },
            };
          } else {
            sampleData = {
              ...sampleData,
              [element.placeHolderText]:
                element.type === 'Date'
                  ? { key: element.placeHolderText, value: getTodaysDateWithFormat() }
                  : { key: element.placeHolderText, value: '' },
            };
          }
        });
        return sampleData;
      };
      let designSampleData = getSampleData(documentVariables);
      setExamplValuesData(designSampleData);
    }
  }, [documentVariables]);

  const constructChartVariable = (variables, isFormTable = false) => {
    const source = {};
    const header = [];
    variables.forEach(item => {
      header.push(item.placeHolderText);
      Object.keys(item).forEach(key => {
        if (key === 'placeHolderText') {
          if (isFormTable) {
            if (item.placeHolderText === 'selected') {
              source[item.placeHolderText] = 'false';
            } else {
              source[item.placeHolderText] = '';
            }
          } else {
            source[item.placeHolderText] = '';
          }
        }
      });
    });
    return { header, source: [source], isObject: true };
  };

  const handleGenerateForm = async () => {
    const isContractFetched = await fetchConsolidatedVariables();
    if (isContractFetched) {
      setShowForm(true);
      setShowDocument(false);
    }
  };

  const getAllLanguages = async () => {
    const response = await getAllLanguage();
    if (response.data && Array.isArray(response.data)) {
      setLanguages(response.data);
    }
  };

  useEffect(() => {
    getAllLanguages();
  }, []);

  const generateDataSet = (variable, parentDataSet = {}, sampleData = {}, nested = false) => {
    if (variable.type === VARIABLE_TYPE.OBJECT || variable.type === VARIABLE_TYPE.TABLE_OBJECT) {
      let isArray = nested ? variable.isArray || false : true;
      let listArray = [];
      let d = sampleData?.value?.forEach(eachBlock => {
        let blockSamples = {};
        eachBlock.value.forEach(block => {
          if (block.type === VARIABLE_TYPE.CHART_OBJECT) {
            blockSamples[block.name] = block.value.source;
          } else if (block.type === VARIABLE_TYPE.TABLE_OBJECT) {
            blockSamples = generateDataSet(
              variable.variables.find(v => v.placeHolderText === block.name),
              blockSamples,
              block.value,
              variable.type === VARIABLE_TYPE.OBJECT ? !variable.isArray : true,
            );
          } else {
            blockSamples[block.name] = block.value;
          }
        });
        listArray.push(blockSamples);
      });
      parentDataSet = {
        ...parentDataSet,
        [variable.placeHolderText]: isArray ? listArray : listArray[0],
      };
    } else if (
      variable.type === VARIABLE_TYPE.CHART_OBJECT ||
      variable.type === VARIABLE_TYPE.FORM_OBJECT
    ) {
      const listArray = [];
      sampleData &&
        sampleData.value &&
        sampleData.value.forEach(eachBlock => {
          let blockSamples = {};
          eachBlock.value.forEach(block => {
            blockSamples[block.name] = block.value.source;
          });
          listArray.push(blockSamples);
        });
      parentDataSet = {
        ...parentDataSet,
        ...listArray[0],
      };
    } else {
      parentDataSet = {
        ...parentDataSet,
        [variable.placeHolderText]: sampleData?.value || '',
      };
    }
    return parentDataSet;
  };

  const generateTransaction = async () => {
    let dataSet = {
      uniq_id: '',
      uniq_name: '',
    };
    documentVariables.forEach(variable => {
      const key = ALL_VARIABLE_OBJECT_TYPES.includes(variable.type)
        ? variable.name
        : variable.placeHolderText;
      dataSet = generateDataSet(variable, dataSet, examplValuesData[key]);
    });
    const payload = {
      communicationId: communicationDetails.communicationId,
      channel: selectedChannel,
      language: selectedLanguage,
      tagsInfo: [],
      dataSet: dataSet,
    };

    if (isEmail) {
      payload.to = toAddresses;
      payload.cc = ccAddresses;
    } else {
      payload.printable = false;
    }
    setShowDownloadLoader(true);
    try {
      const response = await initiateTransaction(payload);
      const res = getJsonFromArraybuffer(response);
      if (!res?.errorCode) {
        if (!isEmail) {
          saveByteArray(communicationDetails?.communicationName || 'document', response.data);
        }
        setShowDocument(true);
      } else {
        const errorMessages = handleDataValidationErrors(res.data);
        if (Object.keys(errorMessages)?.length > 0) {
          message.error(
            t(
              COMMUNICATION_GENERATOR.VALIDATION_ERROR_MESSAGE.key,
              COMMUNICATION_GENERATOR.VALIDATION_ERROR_MESSAGE.value,
            ),
          );
        } else {
          if (isEmail) {
            message.error(
              t(
                COMMUNICATION_GENERATOR.SENDING_EMAIL_FAILED.key,
                COMMUNICATION_GENERATOR.SENDING_EMAIL_FAILED.value,
              ),
            );
          } else {
            message.error(
              t(
                COMMUNICATION_GENERATOR.DOWNLOADING_DOCUMENT_FAILED.key,
                COMMUNICATION_GENERATOR.DOWNLOADING_DOCUMENT_FAILED.value,
              ),
            );
          }
        }
        setErrorMessages(handleDataValidationErrors(res.data));
      }
    } catch (e) {
      if (isEmail) {
        message.error(
          t(
            COMMUNICATION_GENERATOR.SENDING_EMAIL_FAILED.key,
            COMMUNICATION_GENERATOR.SENDING_EMAIL_FAILED.value,
          ),
        );
      } else {
        message.error(
          t(
            COMMUNICATION_GENERATOR.DOWNLOADING_DOCUMENT_FAILED.key,
            COMMUNICATION_GENERATOR.DOWNLOADING_DOCUMENT_FAILED.value,
          ),
        );
      }
      console.error(e, 'Transaction initiation failed');
    } finally {
      setShowDownloadLoader(false);
    }
  };

  const handleReset = () => {
    setCommunicationDetails(null);
    setSelectedChannel('');
    setToAddresses([]);
    setCcAddresses([]);
    setSelectedLanguage('');
    setShowForm(false);
    setShowDocument(false);
    setFilteredCommunications([]);
  };

  const handleTemplateChange = async templateInfo => {
    try {
      const response = await getCommunicationLanguages(templateInfo.communicationId);
      if (response.statusCode === 3212) {
        const data = response?.data;
        const printLanguages = data?.printLanguages || [];
        const emailLanguages = data?.emailLanguages || [];
        setCommunicationDetails({
          ...templateInfo,
          print: printLanguages,
          email: emailLanguages,
        });
        if (printLanguages.length && !emailLanguages.length) {
          setSelectedChannel('print');
          if (printLanguages.length >= 1) {
            setSelectedLanguage(printLanguages[0]);
          }
        } else if (!printLanguages.length && emailLanguages.length) {
          setSelectedChannel('email');
          if (emailLanguages.length >= 1) {
            setSelectedLanguage(emailLanguages[0]);
          }
        } else {
          setSelectedLanguage('');
        }
      } else {
        message.error(
          t(
            COMMUNICATION_GENERATOR.FAILED_FINDING_COMMUNICATION_INFO.key,
            COMMUNICATION_GENERATOR.FAILED_FINDING_COMMUNICATION_INFO.value,
          ),
        );
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AppContainer>
      <ContentWrapper>
        <AppTitle>
          {t(
            COMMUNICATION_GENERATOR.COMMUNICATION_GENERATOR_HEADING.key,
            COMMUNICATION_GENERATOR.COMMUNICATION_GENERATOR_HEADING.value,
          )}
        </AppTitle>
        <AppSubtitle>
          {t(
            COMMUNICATION_GENERATOR.COMMUNICATION_GENERATOR_DESCRIPTION.key,
            COMMUNICATION_GENERATOR.COMMUNICATION_GENERATOR_DESCRIPTION.value,
          )}
        </AppSubtitle>

        {!showForm && !showDocument && (
          <TemplateSelector
            loader={showLoader}
            communicationDetails={communicationDetails}
            filteredCommunications={filteredCommunications}
            setFilteredCommunications={setFilteredCommunications}
            onTemplateChange={handleTemplateChange}
            selectedChannel={selectedChannel}
            onChannelChange={setSelectedChannel}
            toAddresses={toAddresses}
            setToAddresses={setToAddresses}
            ccAddresses={ccAddresses}
            setCcAddresses={setCcAddresses}
            languages={languages}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
            onLanguageChange={setSelectedLanguage}
            onGenerateForm={handleGenerateForm}
          />
        )}

        {communicationDetails && showForm && !showDocument && (
          <DynamicForm
            showDownloadLoader={showDownloadLoader}
            communicationDetails={communicationDetails}
            channel={selectedChannel}
            language={languages.find(lang => lang.code === selectedLanguage)?.name}
            formVariables={formVariables}
            examplValuesData={examplValuesData}
            setExamplValuesData={setExamplValuesData}
            documentVariables={documentVariables}
            errorMessages={errorMessages}
            setErrorMessages={setErrorMessages}
            onBack={() => setShowForm(false)}
            onSubmit={generateTransaction}
          />
        )}

        {communicationDetails && showDocument && (
          <DocumentPreview
            communicationDetails={communicationDetails}
            channel={selectedChannel}
            language={languages.find(lang => lang.code === selectedLanguage)?.name}
            onBack={() => setShowDocument(false)}
            onReset={handleReset}
          />
        )}
      </ContentWrapper>
    </AppContainer>
  );
}

export default CommunicationGenerator;
